import React, { useState } from 'react';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  Menu,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Logout as LogoutIcon, AccountCircle as AccountIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import Logo from '../Logo';
import { ROLE_LABELS, UserRole } from '../../types';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, currentRole, setCurrentRole, signOut } = useAuth();
  const { branding } = useBranding();
  const theme = useTheme();
  const navigate = useNavigate();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const mobileMenuOpen = Boolean(mobileMenuAnchor);

  // Atualiza o título e favicon da página
  useDocumentMeta();

  const handleLogout = () => {
    signOut();
    navigate('/login');
    setMobileMenuAnchor(null);
  };

  const handleRoleChange = (role: UserRole) => {
    setCurrentRole(role);
  };

  const openMobileMenu = (e: React.MouseEvent<HTMLElement>) => setMobileMenuAnchor(e.currentTarget);
  const closeMobileMenu = () => setMobileMenuAnchor(null);

  const onHeader = branding.colors.headerAction;

  const selectSx = {
    color: onHeader,
    '.MuiOutlinedInput-notchedOutline': { borderColor: alpha(onHeader, 0.5) },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: onHeader },
    '.MuiSvgIcon-root': { color: onHeader },
    minWidth: { xs: 120, sm: 140 },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar
          sx={{
            flexWrap: 'wrap',
            gap: { xs: 0.5, sm: 0 },
            py: { xs: 0.5, sm: 0 },
            minHeight: { xs: 48, sm: 64 },
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => navigate('/dashboard')}
          >
            <Logo color={onHeader} />
          </Box>

          {/* Desktop: seletor de perfil, nome e logout */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {user && user.roles.length > 1 && (
              <Select
                value={currentRole}
                onChange={(e) => handleRoleChange(e.target.value as UserRole)}
                size="small"
                sx={{ mr: 2, ...selectSx }}
              >
                {user.roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </MenuItem>
                ))}
              </Select>
            )}

            <Typography
              variant="body2"
              sx={{
                mr: 2,
                color: onHeader,
                fontWeight: 500,
              }}
            >
              {user?.name}
            </Typography>

            <IconButton
              onClick={handleLogout}
              title="Sair"
              size="small"
              sx={{
                color: onHeader,
                '&:hover': { bgcolor: alpha(onHeader, 0.12) },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Box>

          {/* Mobile: ícone que abre menu com perfil, troca de role e sair */}
          <IconButton
            onClick={openMobileMenu}
            aria-label="menu da conta"
            aria-controls={mobileMenuOpen ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={mobileMenuOpen ? 'true' : undefined}
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              color: onHeader,
              '&:hover': { bgcolor: alpha(onHeader, 0.12) },
            }}
          >
            <AccountIcon />
          </IconButton>
        </Toolbar>

        <Menu
          id="account-menu"
          anchorEl={mobileMenuAnchor}
          open={mobileMenuOpen}
          onClose={closeMobileMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 220, mt: 1.5 } } }}
        >
          {user?.name && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Logado como
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {user.name}
              </Typography>
            </Box>
          )}
          {user && user.roles.length > 1 && (
            <>
              <Divider />
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Perfil
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={currentRole}
                  onChange={(e) => {
                    handleRoleChange(e.target.value as UserRole);
                    closeMobileMenu();
                  }}
                >
                  {user.roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </>
          )}
          <Divider />
          <MenuItem
            onClick={handleLogout}
            sx={{ gap: 1, color: 'error.main' }}
          >
            <LogoutIcon fontSize="small" />
            Sair
          </MenuItem>
        </Menu>
      </AppBar>

      <Container component="main" sx={{ flex: 1, py: 4, bgcolor: theme.palette.background.default }}>
        {children}
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          bgcolor: theme.palette.background.default,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {branding.platformName} © {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );
};

export default Layout;
